import React, { createContext, useContext, useEffect, useState } from "react";

interface ExchangeRateContextProps {
  exchangeRates: Record<string, number>;
  lastUpdated: string | null;
}

const ExchangeRateContext = createContext<ExchangeRateContextProps | undefined>(undefined);

export const ExchangeRateProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const fetchExchangeRates = async () => {
    try {
      // เปลี่ยน URL เป็นตัวใหม่ที่คุณต้องการ
      const response = await fetch("https://api.frankfurter.dev/v2/rates?base=USD&quotes=THB");
      const data = await response.json();

      // เนื่องจาก data ที่ได้เป็น Array: [{ date, base, quote, rate }]
      // เราจะดึงข้อมูลตัวแรกออกมา
      if (data && data.length > 0) {
        const rateInfo = data[0];
        const newRates = { [rateInfo.quote]: rateInfo.rate }; // สร้าง Object { THB: 32.115 }
        const newDate = rateInfo.date;

        setExchangeRates(newRates);
        setLastUpdated(newDate);

        // เก็บลง LocalStorage
        localStorage.setItem("exchangeRates", JSON.stringify(newRates));
        localStorage.setItem("lastUpdated", newDate);
      }
    } catch (error) {
      console.error("Failed to fetch exchange rates:", error);
    }
  };

  useEffect(() => {
    const storedRates = localStorage.getItem("exchangeRates");
    const storedDate = localStorage.getItem("lastUpdated");
    const today = new Date().toISOString().split("T")[0];

    if (storedRates && storedDate === today) {
      setExchangeRates(JSON.parse(storedRates));
      setLastUpdated(storedDate);
    } else {
      fetchExchangeRates();
    }

    const interval = setInterval(() => {
      const currentDate = new Date().toISOString().split("T")[0];
      // ตรวจสอบจาก state ล่าสุด (ใช้ functional update หรือตรวจสอบเงื่อนไขให้ดี)
      if (currentDate !== storedDate) {
        fetchExchangeRates();
      }
    }, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, []); // ลบ lastUpdated ออกจาก dependency เพื่อไม่ให้เกิด loop ที่ไม่จำเป็น

  return (
    <ExchangeRateContext.Provider value={{ exchangeRates, lastUpdated }}>
      {children}
    </ExchangeRateContext.Provider>
  );
};

export const useExchangeRates = () => {
  const context = useContext(ExchangeRateContext);
  if (!context) {
    throw new Error("useExchangeRates must be used within an ExchangeRateProvider");
  }
  return context;
};